
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  schema?: object;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema
}) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
      // Update OG title
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', title);
      // Update Twitter title
      const twitterTitle = document.querySelector('meta[property="twitter:title"]');
      if (twitterTitle) twitterTitle.setAttribute('content', title);
    }

    // Update description
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
      
      // Update OG description
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', description);
      // Update Twitter description
      const twitterDesc = document.querySelector('meta[property="twitter:description"]');
      if (twitterDesc) twitterDesc.setAttribute('content', description);
    }

    // Update keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }

    // Update URL
    if (url) {
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', url);
      const twitterUrl = document.querySelector('meta[property="twitter:url"]');
      if (twitterUrl) twitterUrl.setAttribute('content', url);
      
      // Update canonical
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    }

    // Update image
    if (image) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute('content', image);
      const twitterImage = document.querySelector('meta[property="twitter:image"]');
      if (twitterImage) twitterImage.setAttribute('content', image);
    }

    // Update type
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) ogType.setAttribute('content', type);

    // Add JSON-LD Schema
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;;
      if (schemaScript) {
        document.head.removeChild(schemaScript);
      }
      
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }
  }, [title, description, keywords, image, url, type, schema]);

  return null;
};

export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://balamtech.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Balam Technology",
    "alternateName": "Balam Tech",
    "url": siteUrl,
    "logo": `${siteUrl}/favicon.png`,
    "description": "Custom software development company specializing in web applications, APIs, and modern technology solutions.",
    "foundingDate": "2024",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-123-4567",
      "contactType": "Customer Service",
      "email": "hello@balamtech.com",
      "availableLanguage": ["English", "Spanish"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "sameAs": [
      "https://linkedin.com/company/balamtech",
      "https://twitter.com/balamtech",
      "https://github.com/balamtech"
    ],
    "serviceArea": {
      "@type": "Place",
      "name": "Worldwide"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Software Development Services",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Website Development",
          "description": "Modern UI with responsive design and optimized performance"
        },
        {
          "@type": "OfferCatalog",
          "name": "Web Applications",
          "description": "Scalable web apps with modern architecture and UX"
        },
        {
          "@type": "OfferCatalog",
          "name": "API Development",
          "description": "Secure APIs with robust documentation and monitoring"
        },
        {
          "@type": "OfferCatalog",
          "name": "Landing Pages",
          "description": "High-converting pages that drive results for your business"
        }
      ]
    }
  };
};

export const generateWebsiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://balamtech.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Balam Technology",
    "url": siteUrl,
    "description": "Custom software development company building innovative solutions with modern technologies.",
    "publisher": {
      "@type": "Organization",
      "name": "Balam Technology"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

export const generateServiceSchema = (service: {
  name: string;
  description: string;
  features: string[];
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://balamtech.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": "Balam Technology",
      "url": siteUrl
    },
    "serviceType": "Software Development",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": service.name,
      "itemListElement": service.features.map((feature, index) => ({
        "@type": "Offer",
        "name": feature,
        "position": index + 1
      }))
    },
    "areaServed": {
      "@type": "Place",
      "name": "Worldwide"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": `${siteUrl}#contact`,
      "serviceSupportedDevice": "Desktop, Mobile"
    }
  };
};
